import type { ChangelogDiff } from './types';

type ChangePath = NonNullable<ChangelogDiff['path']>;

/** Keys only present in a serialized mongoose Schema: any of them identifies the object as a schema */
const MONGOOSE_SCHEMA_MARKERS = ['$globalPluginsApplied', 'callQueue', 'childSchemas'].map(key => key.toLowerCase());

/** Internal keys of a serialized mongoose Schema (historical changelogs stored whole schemas by mistake) */
const MONGOOSE_SCHEMA_KEYS = [
  '$connectionPluginsApplied',
  '$globalPluginsApplied',
  '$id',
  '_applyDiscriminators',
  '_indexes',
  '_requiredpaths',
  '_userProvidedOptions',
  'aliases',
  'callQueue',
  'childSchemas',
  'inherits',
  'mapPaths',
  'methodOptions',
  'methods',
  'nested',
  'obj',
  'options',
  'paths',
  'plugins',
  'query',
  's',
  'singleNestedPaths',
  'statics',
  'subpaths',
  'tree',
  'virtuals'
].map(key => key.toLowerCase());

/** Changes without meaning for the user: generated list ids, segment ids duplicated in `segments`, segment metadata */
const IGNORED_PATHS = [
  /^targeting\.audience\.lists\.\d+\.(id|segmentIds)(\.|$)/i,
  /^targeting\.audience\.lists\.\d+\.segments\.\d+\.(?!name$)/i
];

/** Keys are compared case insensitively */
function toKey(key: number | string | undefined): string {
  return String(key).toLowerCase();
}

/** Paths of the serialized schemas: the parent path of every schema marker */
function getSchemaPaths(changes: ChangelogDiff[]): ChangePath[] {
  return changes.flatMap(({ path = [] }) => {
    const markerIndex = path.findIndex(key => MONGOOSE_SCHEMA_MARKERS.includes(toKey(key)));
    return markerIndex === -1 ? [] : [path.slice(0, markerIndex)];
  });
}

function isSchemaInternal(path: ChangePath, schemaPaths: ChangePath[]): boolean {
  return schemaPaths.some(
    schemaPath =>
      path.length > schemaPath.length &&
      schemaPath.every((key, index) => key === path[index]) &&
      MONGOOSE_SCHEMA_KEYS.includes(toKey(path[schemaPath.length]))
  );
}

/** Removes the changes that carry no information for the user */
export function removeNoise(changes: ChangelogDiff[]): ChangelogDiff[] {
  const schemaPaths = getSchemaPaths(changes);
  return changes.filter(({ path = [] }) => {
    const pathString = path.join('.');
    return !IGNORED_PATHS.some(regex => regex.test(pathString)) && !isSchemaInternal(path, schemaPaths);
  });
}
